/**
 * Client-side ISO-week helpers for the Weekly Summary navigation (CLE-74 §3).
 *
 * The backend is the source of truth for which week a payload represents
 * (`weekParam`, e.g. "2026-W24"). These helpers only need to step that label
 * one week back/forward for the prev/next controls and render human dates, all
 * in UTC so they line up with the server's Mon–Sun windows.
 */

const MS_PER_DAY = 86_400_000;

/** Monday (UTC) of the ISO week `YYYY-Www`, or null when malformed. */
export function weekParamToMonday(week: string): Date | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(week.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const wk = Number(m[2]);
  if (wk < 1 || wk > 53) return null;
  // ISO week 1 contains Jan 4th; find its Monday, then add (wk-1) weeks.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = (jan4.getUTCDay() + 6) % 7; // 0=Mon … 6=Sun
  const week1Monday = new Date(jan4.getTime() - jan4Dow * MS_PER_DAY);
  return new Date(week1Monday.getTime() + (wk - 1) * 7 * MS_PER_DAY);
}

/** ISO week label ("YYYY-Www") for the week containing `ref` (UTC). */
export function isoWeekString(ref: Date): string {
  const d = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate()));
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow + 3); // Thursday determines the ISO year
  const isoYear = d.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstDow = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDow + 3);
  const week = 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * MS_PER_DAY));
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}

/** The ISO week label `delta` weeks away from `week` (delta may be negative). */
export function shiftWeekParam(week: string, delta: number): string | null {
  const monday = weekParamToMonday(week);
  if (!monday) return null;
  return isoWeekString(new Date(monday.getTime() + delta * 7 * MS_PER_DAY));
}

/** "Lun, 9 iun." for a YYYY-MM-DD day key, in UTC so it matches the server day. */
export function formatDayLabel(dayKey: string): string {
  return new Intl.DateTimeFormat('ro-RO', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${dayKey}T00:00:00.000Z`));
}

/** "9 iun." for an ISO timestamp, UTC. Used for per-task transition dates. */
export function formatTransitionDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ro-RO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

/** "8–14 iun. 2026" range label for a Mon–Sun week (YYYY-MM-DD bounds). */
export function formatWeekRange(weekStart: string, weekEnd: string): string {
  const start = new Date(`${weekStart}T00:00:00.000Z`);
  const end = new Date(`${weekEnd}T00:00:00.000Z`);
  const sameMonth =
    start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();
  const dayFmt = new Intl.DateTimeFormat('ro-RO', { day: 'numeric', timeZone: 'UTC' });
  const dayMonthFmt = new Intl.DateTimeFormat('ro-RO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const fullFmt = new Intl.DateTimeFormat('ro-RO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return sameMonth
    ? `${dayFmt.format(start)}–${fullFmt.format(end)}`
    : `${dayMonthFmt.format(start)} – ${fullFmt.format(end)}`;
}

/** The two ways of reading the week: a Mon→Sun timeline, or grouped by status. */
export type WeeklyView = 'day' | 'status';
